/**
 * Theme system — named presets that override the CSS custom properties
 * defined in `@theme`. Runtime toggle stores the chosen preset + font in
 * localStorage and applies overrides on `:root` via `style.setProperty`.
 *
 * In M3 this moves to per-user account preferences.
 */

export type ThemePreset = {
  id: string
  name: string
  description: string
  vars: Record<string, string>
}

export const THEME_PRESETS: ThemePreset[] = [
  {
    id: 'paper',
    name: 'Paper',
    description: 'Warm off-white with a terracotta accent. The default.',
    vars: {
      '--color-bg': '#f3f1ec',
      '--color-paper': '#ffffff',
      '--color-paper-edge': '#f9f7f2',
      '--color-surface-hover': '#ece9e1',
      '--color-ink': '#26211c',
      '--color-ink-soft': '#3a332c',
      '--color-ink-muted': '#8a8176',
      '--color-border': '#e4ded1',
      '--color-accent': '#b6632a',
    },
  },
  {
    id: 'plain',
    name: 'Plain',
    description: 'Neutral grays, blue accent. Spare and focused.',
    vars: {
      '--color-bg': '#f4f4f4',
      '--color-paper': '#ffffff',
      '--color-paper-edge': '#f7f7f7',
      '--color-surface-hover': '#ebebeb',
      '--color-ink': '#1a1a1a',
      '--color-ink-soft': '#2e2e2e',
      '--color-ink-muted': '#8a8a8a',
      '--color-border': '#e2e2e2',
      '--color-accent': '#3c6fbf',
    },
  },
  {
    id: 'night',
    name: 'Night',
    description: 'Low-light dark theme.',
    vars: {
      '--color-bg': '#151311',
      '--color-paper': '#1c1a17',
      '--color-paper-edge': '#201d1a',
      '--color-surface-hover': '#2a2621',
      '--color-ink': '#ebe6db',
      '--color-ink-soft': '#c8c1b2',
      '--color-ink-muted': '#7d766a',
      '--color-border': '#2e2923',
      '--color-accent': '#d4834a',
    },
  },
  {
    id: 'sepia',
    name: 'Sepia',
    description: 'Old-paper amber wash.',
    vars: {
      '--color-bg': '#ece0c2',
      '--color-paper': '#f6e9c8',
      '--color-paper-edge': '#eddfbc',
      '--color-surface-hover': '#e4d4ad',
      '--color-ink': '#2a1f13',
      '--color-ink-soft': '#3f3120',
      '--color-ink-muted': '#8a7956',
      '--color-border': '#d6c59e',
      '--color-accent': '#9a4b1d',
    },
  },
]

export type FontOption = {
  id: string
  name: string
  stack: string
  /** Optional Google Fonts URL to load */
  googleFont?: string
}

export const FONT_OPTIONS: FontOption[] = [
  {
    id: 'source-serif',
    name: 'Source Serif 4',
    stack: '"Source Serif 4", "Iowan Old Style", Charter, Georgia, serif',
    googleFont: 'Source+Serif+4:ital,wght@0,400;0,500;0,600;1,400',
  },
  {
    id: 'lora',
    name: 'Lora',
    stack: 'Lora, Georgia, serif',
    googleFont: 'Lora:ital,wght@0,400;0,500;0,600;1,400',
  },
  {
    id: 'spectral',
    name: 'Spectral',
    stack: 'Spectral, Georgia, serif',
    googleFont: 'Spectral:ital,wght@0,400;0,500;0,600;1,400',
  },
  {
    id: 'eb-garamond',
    name: 'EB Garamond',
    stack: '"EB Garamond", Georgia, serif',
    googleFont: 'EB+Garamond:ital,wght@0,400;0,500;0,600;1,400',
  },
  {
    id: 'charter',
    name: 'Charter (system)',
    stack: '"Iowan Old Style", Charter, Georgia, Cambria, serif',
  },
  {
    id: 'georgia',
    name: 'Georgia',
    stack: 'Georgia, Cambria, serif',
  },
]

const THEME_KEY = 'writers-block:theme'
const FONT_KEY = 'writers-block:font'

export function getThemeId(): string {
  return localStorage.getItem(THEME_KEY) ?? 'paper'
}

export function setThemeId(id: string): void {
  localStorage.setItem(THEME_KEY, id)
  applyTheme()
}

export function getFontId(): string {
  return localStorage.getItem(FONT_KEY) ?? 'source-serif'
}

export function setFontId(id: string): void {
  localStorage.setItem(FONT_KEY, id)
  applyTheme()
}

let loadedGoogleFonts = new Set<string>()

export function loadGoogleFont(googleFont: string) {
  if (loadedGoogleFonts.has(googleFont)) return
  loadedGoogleFonts.add(googleFont)
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = `https://fonts.googleapis.com/css2?family=${googleFont}&display=swap`
  document.head.appendChild(link)
}

/** Preload every FONT_OPTION google font — useful on the settings page
 *  so hover-preview doesn't flash a fallback. */
export function preloadAllFonts() {
  for (const f of FONT_OPTIONS) {
    if (f.googleFont) loadGoogleFont(f.googleFont)
  }
}

/**
 * Apply the saved theme + font to `:root`. Safe to call repeatedly.
 * Should be invoked once at app boot and whenever the selection changes.
 */
export function applyTheme(): void {
  const preset =
    THEME_PRESETS.find((p) => p.id === getThemeId()) ?? THEME_PRESETS[0]
  const root = document.documentElement
  for (const [k, v] of Object.entries(preset.vars)) {
    root.style.setProperty(k, v)
  }
  const font =
    FONT_OPTIONS.find((f) => f.id === getFontId()) ?? FONT_OPTIONS[0]
  if (font.googleFont) loadGoogleFont(font.googleFont)
  root.style.setProperty('--font-serif', font.stack)
}
